import React from "react";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import {
  Button,
  makeStyles,
  useTheme,
  Drawer,
  List,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  ListItemSecondaryAction,
  Box,
  Typography,
  Popover,
} from "@material-ui/core";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { MdRestaurantMenu } from "react-icons/md";
import { FiLogIn, FiHome, FiList } from "react-icons/fi";
import mixpanel from "mixpanel-browser";
import { AiFillCrown } from "react-icons/ai";

import { useReactPWAInstall } from "../../../Components/PwaInstall";
import logo from "../../../logo-icon.png";
import UpgradeCta from "../../../Components/UpgradeCta";
import { mixPanelEvents } from "../../../Constants";
import useSubscription from "../../../Utils/useSubscription";
import { Install } from "./styles";

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  appBar: {
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: drawerWidth,
  },
  title: {
    flexGrow: 1,
  },
  hide: {
    display: "none",
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-start",
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginRight: -drawerWidth,
  },
  contentShift: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
}));

export default function Menu({ open, onClose = () => {} }) {
  const { pwaInstall, supported, isInstalled } = useReactPWAInstall();
  const history = useHistory();
  const dispatch = useDispatch();
  const classes = useStyles();
  const { isTrial, isSubscriber } = useSubscription();
  const linkMenu = useSelector((state) => state.store.alias);
  const isActive = useSelector((state) => state.store.is_active) || false;
  const [anchorEl, setAnchorEl] = React.useState(false);

  const handleSubscriptionPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const openPopover = Boolean(anchorEl);

  const handleIsActiveChange = (event) => {
    const closeStore = !event.target.checked;
    if (closeStore) return dispatch({ type: "CLOSE_STORE" });

    if (isTrial || isSubscriber) return dispatch({ type: "OPEN_STORE" });

    handleSubscriptionPopover(event);
  };

  function handleLogout() {
    localStorage.removeItem("token");
    window.location = "/#/login";
  }

  const handleDrawerClose = () => {
    onClose();
  };

  function handleClickItem(route) {
    history.push(route);
    onClose();
  }

  const handleClickOpenPwaInstall = () => {
    pwaInstall({
      title: "Instalar Web App",
      logo: logo,
      description:
        "Instalar o Portal Mindi vai facilitar o acesso no dia-a-dia do seu trabalho. ",
    })
      .then(() => alert("App instalado com sucesso"))
      .catch(() => {});
  };

  return (
    <Drawer
      className={classes.drawer}
      variant="persistent"
      anchor="left"
      open={open}
      onClose={handleDrawerClose}
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      <div className={classes.drawerHeader}>
        <IconButton style={{ color: "#2dad67" }} onClick={handleDrawerClose}>
          <FiChevronLeft />
        </IconButton>
      </div>
      <Divider />
      <List>
        <ListItem divider button onClick={() => handleClickItem("/plans")}>
          <UpgradeCta onClick={onClose} />
        </ListItem>

        <ListItem button onClick={() => handleClickItem("/cardapio")}>
          <ListItemIcon>
            <MdRestaurantMenu />
          </ListItemIcon>
          <ListItemText primary={`Cardápio`} />
        </ListItem>
        <ListItem button onClick={() => handleClickItem("/establishment")}>
          <ListItemIcon>
            <FiHome />
          </ListItemIcon>
          <ListItemText primary={`Estabelecimento`} />
        </ListItem>
        <ListItem button onClick={() => handleClickItem("/attendance")}>
          <ListItemIcon>
            <FiLogIn />
          </ListItemIcon>
          <ListItemText primary={`Config. Atendimento`} />
        </ListItem>
        <ListItem
          button
          onClick={() => {
            mixpanel.track(mixPanelEvents.GO_TO_ORDER_MANAGER);
            window.location = window.location.origin + "/m";
          }}
        >
          <ListItemIcon>
            <FiList />
          </ListItemIcon>
          <ListItemText primary={`Pedidos`} />
          <ListItemSecondaryAction>
            <AiFillCrown color="orange" size="30px" />
          </ListItemSecondaryAction>
        </ListItem>
      </List>
      <Divider />
      <List>
        <ListItem button>
          <ListItemText primary="Abrir/Fechar" />
          <ListItemSecondaryAction>
            <Switch
              color="primary"
              checked={isActive}
              onChange={handleIsActiveChange}
            />
          </ListItemSecondaryAction>
        </ListItem>
        {linkMenu && (
          <ListItem
            button
            onClick={() => (window.location.href = `https://${linkMenu}`)}
          >
            <ListItemText primary={`Acessar Cardápio`} />
          </ListItem>
        )}
        <ListItem button onClick={() => handleClickItem("/account")}>
          <ListItemText primary={`Minha conta`} />
        </ListItem>
        <ListItem button onClick={handleLogout}>
          <ListItemText primary={`Sair`} />
        </ListItem>
      </List>
      <Divider />
      {supported() && !isInstalled() && (
        <Install>
          <div>
            <img src={logo} />
            <p>Seja mais produtivo com o nosso App!</p>
          </div>
          <div>
            <Button
              onClick={handleClickOpenPwaInstall}
              size="small"
              color="primary"
              variant="outlined"
            >
              Instalar
            </Button>
          </div>
        </Install>
      )}

      <Popover
        open={openPopover}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <Box style={{ padding: 10 }}>
          <Typography>
            A sua assinatura Expirou. <br />
            Clique no botão abaixo para renovar.
          </Typography>
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                history.push("/plans");
                handlePopoverClose();
              }}
            >
              Renovar
            </Button>
          </Box>
        </Box>
      </Popover>
    </Drawer>
  );
}
